import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Package,
  Search,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  CreditCard,
  User,
  MapPin,
  Phone,
  Hash,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { OrderTimeline } from "@/components/OrderTimeLine";
import { format } from "date-fns";
import { id } from "date-fns/locale";

interface Order {
  id: string;
  order_code: string;
  product_id: string;
  buyer_name: string;
  buyer_address: string;
  buyer_employee_number: string;
  buyer_phone: string;
  quantity: number;
  total_price: number;
  payment_method: string;
  payment_proof_url: string;
  status: string;
  created_at: string;
  updated_at: string;
  product?: {
    name: string;
    image_url: string;
    price: number;
  };
}

const OrderTrackingPage = () => {
  const [orderCode, setOrderCode] = useState("");
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const trackOrder = async () => {
    if (!orderCode.trim() || !employeeNumber.trim()) {
      setError("Harap masukkan kode order dan nomor karyawan");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const { data, error: supabaseError } = await supabase
        .from("orders")
        .select(
          `
          *,
          product:products(
            name,
            image_url,
            price
          )
        `
        )
        .eq("order_code", orderCode.trim())
        .eq("buyer_employee_number", employeeNumber.trim())
        .single();

      if (supabaseError) throw supabaseError;
      if (!data) throw new Error("Order tidak ditemukan");

      setOrder(data as Order);
    } catch (err) {
      console.error("Error fetching order:", err);
      setError("Order tidak ditemukan. Silakan cek detail Anda dan coba lagi.");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMMM yyyy HH:mm", { locale: id });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case "transfer bank":
        return <CreditCard className="w-4 h-4 mr-2" />;
      default:
        return <CreditCard className="w-4 h-4 mr-2" />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-3xl mx-auto">
        {/* Button Back */}
        <Button
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate("/"); //Back ke halaman utama
            }
          }}
          variant="outline"
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Kembali
        </Button>

        <Card className="border-none shadow-none">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold tracking-tight">
              Lacak Pesanan Anda
            </CardTitle>
            <CardDescription>
              Masukkan kode order dan nomor karyawan untuk memeriksa status
              pesanan
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Search Form */}
            <div className="space-y-4 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    placeholder="Kode Order (contoh: ORD-12345)"
                    value={orderCode}
                    onChange={(e) => setOrderCode(e.target.value)}
                  />
                </div>
                <div>
                  <Input
                    placeholder="Nomor Karyawan"
                    value={employeeNumber}
                    onChange={(e) => setEmployeeNumber(e.target.value)}
                  />
                </div>
              </div>
              <Button
                onClick={trackOrder}
                disabled={loading}
                className="w-full md:w-auto"
              >
                {loading ? (
                  <>
                    <Search className="w-4 h-4 mr-2 animate-spin" />
                    Mencari...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Lacak Pesanan
                  </>
                )}
              </Button>
              {error && (
                <p className="text-sm text-destructive text-center">{error}</p>
              )}
            </div>

            {/* Order Details */}
            {order && (
              <div className="space-y-8">
                <Card>
                  <CardHeader className="border-b">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div>
                        <CardTitle className="text-lg">
                          Order #{order.order_code}
                        </CardTitle>
                        <CardDescription>
                          Dibuat pada {formatDate(order.created_at)}
                        </CardDescription>
                      </div>
                      <Badge
                        variant={
                          order.status === "completed"
                            ? "default"
                            : order.status === "cancelled"
                            ? "destructive"
                            : "default"
                        }
                        className="mt-2 md:mt-0"
                      >
                        {order.status === "pending" && "Menunggu"}
                        {order.status === "confirmed" && "Dikonfirmasi"}
                        {order.status === "processing" && "Diproses"}
                        {order.status === "shipped" && "Dikirim"}
                        {order.status === "completed" && "Selesai"}
                        {order.status === "cancelled" && "Dibatalkan"}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* Order Timeline */}
                      <div>
                        <h3 className="font-medium mb-4">Status Pesanan</h3>
                        <OrderTimeline status={order.status} updates={[]} />
                      </div>

                      {/* Buyer Info */}
                      <div>
                        <h3 className="font-medium mb-4">Informasi Pembeli</h3>
                        <div className="space-y-4">
                          <div className="flex items-start">
                            <User className="w-5 h-5 mr-3 mt-0.5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{order.buyer_name}</p>
                              <p className="text-sm text-muted-foreground">
                                Karyawan #{order.buyer_employee_number}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start">
                            <MapPin className="w-5 h-5 mr-3 mt-0.5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">Alamat Pengiriman</p>
                              <p className="text-sm text-muted-foreground">
                                {order.buyer_address}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start">
                            <Phone className="w-5 h-5 mr-3 mt-0.5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">Kontak</p>
                              <p className="text-sm text-muted-foreground">
                                {order.buyer_phone}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start">
                            <CreditCard className="w-5 h-5 mr-3 mt-0.5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">Pembayaran</p>
                              <p className="text-sm text-muted-foreground">
                                {order.payment_method}
                              </p>
                              {order.payment_proof_url && (
                                <a
                                  href={order.payment_proof_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary text-sm underline mt-1 inline-block"
                                >
                                  Lihat Bukti Pembayaran
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="mt-8">
                      <h3 className="font-medium mb-4">Detail Produk</h3>
                      <div className="border rounded-lg divide-y">
                        <div className="flex flex-wrap items-start p-4 hover:bg-muted/50">
                          {order.product?.image_url ? (
                            <div className="flex-shrink-0 h-16 w-16 rounded-md overflow-hidden border">
                              <img
                                src={order.product.image_url}
                                alt={order.product.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="flex-shrink-0 h-16 w-16 rounded-md overflow-hidden border bg-muted flex items-center justify-center">
                              <Package className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                          <div className="ml-4 flex-1">
                            <h4 className="font-medium">
                              {order.product?.name || "Produk tidak ditemukan"}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {formatCurrency(order.product?.price || 0)} ×{" "}
                              {order.quantity}
                            </p>
                          </div>
                          <div className="ml-4">
                            <p className="font-medium">
                              {formatCurrency(order.total_price)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Order Summary */}
                    <div className="mt-6 flex justify-end">
                      <div className="w-full md:w-64 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Subtotal
                          </span>
                          <span>
                            {formatCurrency(
                              (order.product?.price || 0) * order.quantity
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between font-bold text-lg pt-2 border-t">
                          <span>Total</span>
                          <span>{formatCurrency(order.total_price)}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="text-center text-sm text-muted-foreground">
                  Butuh bantuan?{" "}
                  <a href="/contact" className="text-primary underline">
                    Hubungi tim support kami
                  </a>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrderTrackingPage;
